import { useEffect, useRef } from 'react';

/** Fallback polling interval when CometD is unavailable (2 minutes). */
export const ONGOING_ORDER_POLL_MS = 120000;

/**
 * Polls a callback on an interval while enabled (e.g. ongoing order pages).
 * Does not manage loading UI — callers should keep poll handlers silent.
 */
export function useOrderPolling(
  pollFn: () => void | Promise<void>,
  enabled: boolean,
  intervalMs: number = ONGOING_ORDER_POLL_MS
): void {
  const pollRef = useRef(pollFn);
  pollRef.current = pollFn;

  useEffect(() => {
    if (!enabled) return;

    const run = () => {
      try {
        const result = pollRef.current();
        if (result && typeof (result as Promise<void>).then === 'function') {
          (result as Promise<void>).catch(() => undefined);
        }
      } catch {
        // ignore polling errors
      }
    };

    const id = window.setInterval(run, intervalMs);
    return () => window.clearInterval(id);
  }, [enabled, intervalMs]);
}

export default useOrderPolling;
